import { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { Button, Input, LoadingAnimatedIcon, LoadingAnimatedText } from '@toolbox/components';
import { QuestionLayout } from '../components/QuestionLayout/QuestionLayout';
import { stringOrUndefined } from '../storyblok/utils/stringOrUndefined';
import { useLeadData, useWidgetContext } from '@widget/core';
import { useTimeslots } from '@widget/core/TimeSlotsProvider';
import Timeslots from '@toolbox/components/Timeslots/Timeslots';
import { getTimeSlotDays } from '@widget/utils/getTimeSlotDays';
import { APPOINTMENT_TYPE } from '@widget/api/types';
import { bookTimeSlot } from '@widget/api/timeSlots';

import type { TimeSlotType } from '@widget/api/types';
import type * as React from 'react';
import type { BookBrokerAppointmentStepSchema, QuestionLayoutSchema } from '../storyblok/schemas';
import type { Richtext } from 'storyblok-js-client';
import type { z } from 'zod';

type StepProps = z.infer<typeof BookBrokerAppointmentStepSchema>;
type QuestionLayoutProps = z.infer<typeof QuestionLayoutSchema>;

const SUBMIT_BUTTON_LABEL_DEFAULT = 'Termin vereinbaren' as const;
const PHONE_BUTTON_LABEL_DEFAULT = 'Telefonnummer' as const;

export const BookBrokerAppointmentStep: React.FC<StepProps> = ({
  type = APPOINTMENT_TYPE.CALL,
  ...props
}) => {
  const questionLayoutProps = props.children[0] as QuestionLayoutProps;
  const widget = useWidgetContext();
  const { timeslots, timezone } = useTimeslots();
  const { leadData } = useLeadData();

  const [mutLoading, setMutLoading] = useState(false);
  const [date, setDate] = useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeslot] = useState<null | TimeSlotType>(null);
  const submitButtonRef = useRef<null | HTMLButtonElement>(null);

  const days = useMemo(() => {
    if (timeslots !== null && type !== null) {
      const ts = timeslots[type]?.timeslots || {};
      const { days, firstTimeslotDate } = getTimeSlotDays(ts, timezone);
      setDate(firstTimeslotDate);
      return days;
    }
    return undefined;
  }, [timeslots, type]);

  const handleDateChange = (date: string) => {
    setSelectedTimeslot(null);
    setDate(date);
  };
  const handleSubmit = async () => {
    if (selectedTimeSlot !== null && leadData?.id !== undefined && type !== null) {
      setMutLoading(true);
      try {
        const resp = await bookTimeSlot({
          leadId: leadData?.id,
          email: selectedTimeSlot.email,
          optimal: selectedTimeSlot.isOptimal,
          startTime: selectedTimeSlot.isoDateTime,
          type,
          source: 'widget',
        });

        widget.commit(
          props.id,
          {
            bookBrokerAppointmentTimeSlot: resp.data?.eventId ? selectedTimeSlot : undefined,
            bookingType: type,
          },
          props.nextStep || undefined,
        );
      } catch (e) {
        widget.commit(
          props.id,
          { bookBrokerAppointmentTimeSlot: undefined },
          props.nextStep || undefined,
        );
      }
    }
    // for DAB before lead flow
    if (selectedTimeSlot !== null && type !== null && leadData?.id === undefined) {
      widget.commit(
        props.id,
        {
          booking: {
            email: selectedTimeSlot.email,
            optimal: selectedTimeSlot.isOptimal,
            startTime: selectedTimeSlot.isoDateTime,
            type,
            source: 'widget',
          },
        },
        props.nextStep || undefined,
      );
    }
  };

  return (
    <QuestionLayout
      key={questionLayoutProps._uid}
      back={questionLayoutProps.back}
      submit={questionLayoutProps.submit}
      skip={questionLayoutProps.skip}
      title={questionLayoutProps.title}
      description={questionLayoutProps.description}
      backButtonText={stringOrUndefined(questionLayoutProps.backButtonText)}
      closeButtonText={stringOrUndefined(questionLayoutProps.closeButtonText)}
      submitButtonText={stringOrUndefined(questionLayoutProps.submitButtonText)}
      skipButtonText={stringOrUndefined(questionLayoutProps.skipButtonText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      maxW={!questionLayoutProps.maxW ? undefined : questionLayoutProps.maxW}
      headerType={props.headerType}
    >
      {days !== undefined && !mutLoading ? (
        <div>
          {type === APPOINTMENT_TYPE.CALL &&
            widget?.leadData?.customerContact?.phone !== undefined && (
              <Input
                value={widget.leadData.customerContact.phone}
                label={props.phoneInputLabel || PHONE_BUTTON_LABEL_DEFAULT}
                disabled={true}
              />
            )}

          {timeslots !== null && type !== null && date !== null && !!timeslots[type] && (
            <Timeslots
              onChange={setSelectedTimeslot}
              submitButtonRef={submitButtonRef}
              selectedDate={date}
              selectedTimeslot={selectedTimeSlot}
              timeSlots={timeslots[type]!.timeslots[date].slots}
              dayPickerProps={{
                days,
                onChange: handleDateChange,
              }}
              timezone={timezone}
            />
          )}
          <Box mt={12} mb={4} textAlign={'end'}>
            <Button
              refEl={submitButtonRef}
              size={'large'}
              type={'button'}
              onClick={handleSubmit}
              disabled={selectedTimeSlot === null || mutLoading}
              fullWidthMobile
            >
              {props.submitButtonLabel || SUBMIT_BUTTON_LABEL_DEFAULT}
            </Button>
          </Box>
        </div>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LoadingAnimatedIcon />
            <Box mt={3}>
              <LoadingAnimatedText text={props.loadingText} />
            </Box>
          </Box>
        </Box>
      )}
    </QuestionLayout>
  );
};
